// Nome curto de uma pessoa da equipe, para caber em espaço apertado — puro.
//
// ★★★ A-3 ajuste 4 (11/out/2026, decisão do dono): na caixinha da agenda o SDR
// aparece pelo PRIMEIRO NOME. Nome completo não cabe numa coluna de semana sem
// empurrar o nome do paciente — que é quem identifica a consulta.
//
// ⚠⚠ MAS primeiro nome sozinho PODE SER AMBÍGUO, e aí ele deixa de informar: com
// duas pessoas chamadas Ana na clínica, "Ana" não diz de quem é a consulta. Por
// isso a regra tem duas metades:
//
//   nome único na equipe ATIVA  →  só o primeiro nome          ("Bruno")
//   primeiro nome REPETIDO      →  primeiro nome + inicial     ("Ana P.")
//
// ★ A desambiguação olha só quem está ATIVO: pessoa desativada não disputa nome
// com ninguém, e contá-la encurtaria o nome de quem ficou sem motivo.
//
// ★★★ POR QUE A EQUIPE ENTRA COMO PARÂMETRO, e não é lida aqui: a função fica
// pura e testável sem Firebase, e quem chama já tem a lista em memória (a agenda
// assina `usuarios` desde a WO lote 3c). Ler aqui dentro criaria uma segunda
// fonte da equipe, que é como `sdrNome` e `espaco` divergiram antes.
//
// ⚠ USO HOJE: a agenda e a coluna SDR da lista de leads (28/out, `TabelaLeads`).
// Funil, card do lead e chat continuam com o nome como está — ver
// `docs/a3-testes.md`, "pendência pós-estreia".

/** Minúsculo e sem acento, para "Jéssica" e "jessica" serem o mesmo nome. */
const dobrar = (t) =>
  String(t ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** Aceita string ou objeto com `.nome` — é o que torna a função reutilizável. */
const nomeDe = (pessoa) => {
  if (typeof pessoa === 'string') return pessoa;
  return String(pessoa?.nome ?? '');
};

/**
 * As palavras do nome, sem vazios.
 *
 * ⚠ `split(/\s+/)` e não `split(' ')`: nome vindo de planilha chega com espaço
 * duplo e com tabulação, e um pedaço vazio viraria inicial ".".
 */
const partes = (nome) => String(nome ?? '').trim().split(/\s+/).filter(Boolean);

/** Só o primeiro nome, como está escrito (sem dobrar — dobrar é para comparar). */
export function primeiroNome(pessoa) {
  return partes(nomeDe(pessoa))[0] ?? '';
}

/**
 * Quantas pessoas ATIVAS da equipe têm este primeiro nome.
 *
 * ★ Exportada porque é ela que o teste usa para provar a contagem sem passar
 * pela montagem do nome — e porque quem chamar isto no funil um dia vai querer
 * saber "há ambiguidade?" sem já ter o texto pronto.
 */
export function quantosComOPrimeiroNome(nome, equipe = []) {
  const alvo = dobrar(primeiroNome(nome));
  if (!alvo) return 0;
  return (equipe ?? []).filter((p) => {
    // ⚠ `ativo !== false` e não `ativo === true`: membro antigo pode não ter o
    // campo, e tratá-lo como inativo tiraria a desambiguação de quem existe.
    if (p?.ativo === false) return false;
    return dobrar(primeiroNome(p)) === alvo;
  }).length;
}

/**
 * O nome como ele deve aparecer num espaço apertado.
 *
 * ★ `pessoa` pode ser string (`lead.sdrNome`, denormalizado) ou objeto (`{nome}`).
 * ★ `equipe` é a lista de `usuarios` da clínica; vazia ou ausente devolve só o
 *   primeiro nome — ⚠ **sem equipe não se sabe de ambiguidade**, e inventar a
 *   inicial seria afirmar um conflito que ninguém mediu.
 */
export function nomeCurto(pessoa, equipe = []) {
  const nome = nomeDe(pessoa).trim();
  const pedacos = partes(nome);
  if (pedacos.length === 0) return '';

  const primeiro = pedacos[0];
  if (quantosComOPrimeiroNome(nome, equipe) < 2) return primeiro;

  // ★ Há conflito. A inicial vem do SEGUNDO pedaço do nome desta pessoa.
  // ⚠ Quem só tem um nome não ganha ponto solto ("Ana ."): devolve "Ana" e o
  // conflito fica visível — o que é honesto, porque não há sobrenome que o
  // resolva.
  const segundo = pedacos[1];
  if (!segundo) return primeiro;
  return `${primeiro} ${segundo[0].toUpperCase()}.`;
}
